import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon, CalendarDaysIcon, ChartBarIcon, BanknotesIcon,
  ClipboardDocumentListIcon, MegaphoneIcon, TableCellsIcon,
  UserCircleIcon, XMarkIcon, AcademicCapIcon, BookOpenIcon,
  StarIcon, FingerPrintIcon, ChatBubbleLeftRightIcon, PhotoIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';

const NAV = [
  { section:'Main',     name:'Dashboard',         href:'/parent/dashboard',     icon:HomeIcon },
  { section:'Child',    name:'Attendance',         href:'/parent/attendance',    icon:CalendarDaysIcon },
  { section:'Child',    name:'Gate Log',           href:'/parent/gate-log',      icon:FingerPrintIcon },
  { section:'Child',    name:'Results',            href:'/parent/results',       icon:ChartBarIcon },
  { section:'Child',    name:'Grade Report',       href:'/parent/grade-report',  icon:DocumentChartBarIcon },
  { section:'Child',    name:'Fees',               href:'/parent/fees',          icon:BanknotesIcon },
  { section:'Child',    name:'Timetable',          href:'/parent/timetable',     icon:TableCellsIcon },
  { section:'Child',    name:'Datesheets',         href:'/parent/datesheets',    icon:ClipboardDocumentListIcon },
  { section:'Child',    name:'Messages',           href:'/parent/messages',      icon:ChatBubbleLeftRightIcon, badge:'messages' },
  { section:'Learning', name:'ILP',                href:'/parent/ilp',           icon:ClipboardDocumentListIcon },
  { section:'Learning', name:'Materials',          href:'/parent/materials',     icon:BookOpenIcon },
  { section:'Learning', name:'Class Gallery',      href:'/parent/class-media',   icon:PhotoIcon },
  { section:'Learning', name:'Online Assessments', href:'/parent/quizzes',       icon:AcademicCapIcon },
  { section:'Learning', name:'Notices',            href:'/parent/notices',       icon:MegaphoneIcon, badge:'notices' },
  { section:'Learning', name:'Evaluation',         href:'/parent/feedback',      icon:StarIcon },
  { section:'Account',  name:'Profile',            href:'/parent/profile',       icon:UserCircleIcon },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .psb { width:240px; min-width:240px; height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .psb-brand { padding:24px 20px 20px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; position:relative; }
  .psb-brand-icon { width:34px; height:34px; background:#f97316; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:10px; }
  .psb-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .psb-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .psb-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .psb-close { display:none; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; align-items:center; justify-content:center; }
  .psb-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
  .psb-nav { flex:1; min-height:0; padding:12px; overflow-y:auto; overflow-x:hidden; scrollbar-width:thin; scrollbar-color:#2a2f4e #0f1123; }
  .psb-nav::-webkit-scrollbar { width:4px; }
  .psb-nav::-webkit-scrollbar-thumb { background:#2a2f4e; border-radius:99px; }
  .psb-nav::-webkit-scrollbar-track { background:#0f1123; }
  .psb-section { font-size:.62rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:16px 8px 6px; }
  .psb-link { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.82rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:2px; position:relative; }
  .psb-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .psb-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .psb-link.active { background:rgba(249,115,22,.12); color:#f97316; }
  .psb-link.active::before { content:''; position:absolute; left:-12px; top:25%; bottom:25%; width:3px; background:#f97316; border-radius:0 3px 3px 0; }
  .psb-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .psb-footer { padding:16px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .psb-footer-text { font-size:.68rem; color:#353b5c; text-align:center; }
  .psb-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; line-height:1; flex-shrink:0; }
  @media(max-width:768px) { .psb-close { display:flex; } }
`;

const sectionOrder = ['Main','Child','Learning','Account'];

export default function ParentSidebar({ onClose, activeChild }) {
  const location = useLocation();
  const { brand } = useSchoolBrand();
  const [messageCount, setMessageCount] = useState(0);
  const [noticeCount,  setNoticeCount]  = useState(0);

  useEffect(() => {
    const fetchMessageCount = () => api.get('/chat/unread-count').then(r => setMessageCount(r.data.unreadCount || 0)).catch(() => {});
    fetchMessageCount();
    const id = setInterval(fetchMessageCount, 20000);
    // ChatPage dispatches this the instant a thread gets marked read, so the
    // badge clears immediately instead of waiting up to 20s for the next poll.
    window.addEventListener('chat-unread-changed', fetchMessageCount);
    return () => { clearInterval(id); window.removeEventListener('chat-unread-changed', fetchMessageCount); };
  }, []);

  useEffect(() => {
    api.get('/parent/notices/unread-count').then(r => setNoticeCount(r.data?.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/parent/notices')) {
      api.post('/parent/notices/mark-read').catch(() => {});
      setNoticeCount(0);
    }
  }, [location.pathname]);

  const badgeCount = (key) => (key === 'messages' ? messageCount : key === 'notices' ? noticeCount : 0);

  const sections = sectionOrder
    .map(sec => ({ sec, items: NAV.filter(n => n.section===sec) }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      <style>{style}</style>
      <div className="psb">
        <div className="psb-brand">
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="psb-brand-icon">
                  <svg viewBox="0 0 24 24"><path d="M12 3L2 9l10 6 10-6-10-6zM2 15l10 6 10-6M2 12l10 6 10-6"/></svg>
                </div>
            }
            <div>
              <div className="psb-brand-name">{brand.name || 'School Portal'}</div>
              <div className="psb-brand-sub">Parent Panel</div>
            </div>
          </div>
          {activeChild && (
            <div style={{marginTop:8,fontSize:'.72rem',color:'#f97316',fontWeight:600}}>
              👦 {activeChild.name} — {activeChild.class}{activeChild.section ? ` - ${activeChild.section}` : ''}
            </div>
          )}
          {onClose && <button className="psb-close" onClick={onClose}><XMarkIcon/></button>}
        </div>

        <nav className="psb-nav">
          {sections.map(({ sec, items }) => (
            <div key={sec}>
              <div className="psb-section">{sec}</div>
              {items.map(item => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link key={item.name} to={item.href}
                    className={`psb-link${isActive?' active':''}`}
                    onClick={onClose}>
                    <item.icon/>
                    <span className="psb-link-text">{item.name}</span>
                    {item.badge && badgeCount(item.badge) > 0 && (
                      <span className="psb-badge">{badgeCount(item.badge) > 99 ? '99+' : badgeCount(item.badge)}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="psb-footer">
          <div className="psb-footer-text">v1.0.0 · Parent Portal</div>
        </div>
      </div>
    </>
  );
}